import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { Area, AreaChart, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { getProjectRole } from "../utils/permissions";
import ProjectNav from "../components/ProjectNav";
import { MetricModal } from "./ReportDetail";

const FEEDBACK_TAGS = ["CPI Accuracy", "Schedule Data", "Cost Variance", "Milestone Tracking", "Trend Analysis", "Risk Flags"];

export default function Dashboard() {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedbackTags, setFeedbackTags] = useState<string[]>([]);
  const [feedbackRating, setFeedbackRating] = useState(4);
  const [feedbackNote, setFeedbackNote] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [activeMetric, setActiveMetric] = useState<string | null>(null);
  const [modalFinancials, setModalFinancials] = useState<any>(null);
  const [modalWorkPackages, setModalWorkPackages] = useState<any[]>([]);
  const navigate = useNavigate();
  // Project viewers can only see History
  useEffect(() => {
    const pid = localStorage.getItem("active_project_id") || "";
    if (getProjectRole(pid) === "viewer") {
      navigate("/reports");
    }
  }, []);
  const name = localStorage.getItem("name") || "User";
  const initials = name.split(" ").map((n: string) => n[0]).join("").toUpperCase();
  const projectId = localStorage.getItem("active_project_id") || "";
  const activeProject = JSON.parse(localStorage.getItem("active_project") || "{}");

  useEffect(() => {
    api.get("/api/reports?project_id=" + projectId)
      .then(r => { setReports(r.data); setLoading(false); })
      .catch(() => navigate("/login"));
  }, [navigate]);

  const latest = reports[0];

  useEffect(() => {
    if (!latest?.id) return;
    api.get(`/api/reports/${latest.id}/financials`).then(r => setModalFinancials(r.data)).catch(() => {});
    api.get(`/api/reports/${latest.id}/workpackages`).then(r => setModalWorkPackages(r.data?.work_packages || [])).catch(() => {});
  }, [latest?.id]);
  const metrics = latest?.output?.executive_summary?.metrics;
  const trendData = reports.filter((r: any) => r.status === "published").slice(0, 6).reverse().map((r: any) => {
    const c = r.output?.executive_summary?.metrics?.cpi;
    const s = r.output?.executive_summary?.metrics?.spi;
    return {
      quarter: r.quarter,
      eac: r.output?.executive_summary?.metrics?.eac
        ? r.output.executive_summary.metrics.eac / 1_000_000 : null,
      cei: (c != null && s != null) ? c * s : null,
    };
  });

  const cpi = metrics?.cpi;
  const spi = metrics?.spi;
  const vac = metrics?.vac;
  const eac = metrics?.eac;
  const bac = metrics?.bac;

  // Period-over-period deltas from the two most recent reports
  const prevCpi = reports[1]?.cpi ?? null;
  const prevVac = reports[1]?.vac ?? null;
  const cpiDelta = (cpi != null && prevCpi != null) ? cpi - prevCpi : null;
  const vacDelta = (vac != null && prevVac != null) ? vac - prevVac : null;

  // CPI Health Scale: 0–1.2 range; thresholds at 0.90 (75%) and 0.95 (79.2%)
  const cpiPct = cpi != null ? Math.min(Math.max((cpi / 1.2) * 100, 0), 100) : null;
  const cpiColor = (v: number) => v < 0.9 ? "text-red-500" : v < 0.95 ? "text-amber-500" : "text-green-500";
  const cpiDot = (v: number) => v < 0.9 ? "text-red-500" : v < 0.95 ? "text-amber-500" : "text-green-500";

  // Quick Stats — prefer stored schedule data, fall back to DB-computed counts
  const slippedWps: any[] = latest?.output?.schedule?.slipped_wps || [];
  const slippedCount: number = slippedWps.length > 0 ? slippedWps.length : (latest?.slipped_count ?? 0);
  const maxSlipDays = slippedWps.length > 0 ? Math.max(...slippedWps.map((w: any) => w.slip_days || 0)) : null;
  const earnedPct = metrics?.percent_complete != null
    ? Math.round(metrics.percent_complete * 100)
    : latest?.percent_complete != null
    ? Math.round(latest.percent_complete * 100)
    : null;
  const programStatus = !cpi ? null : cpi < 0.9 || (spi != null && spi < 0.9) ? "At Risk" : cpi < 0.95 || (spi != null && spi < 0.95) ? "Warn" : "Ok";
  const statusStyle = programStatus === "At Risk"
    ? "bg-red-50 text-red-500 border-red-100"
    : programStatus === "Warn"
    ? "bg-amber-50 text-amber-500 border-amber-100"
    : programStatus === "Ok"
    ? "bg-green-50 text-green-600 border-green-100"
    : "bg-gray-50 text-gray-400 border-gray-100";

  const toggleTag = (tag: string) => {
    setFeedbackTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  );

  const metricModalConfigs: Record<string, { title: string; subtitle: string }> = {
    cpi: { title: "CPI", subtitle: "Cost Performance by Dept" },
    spi: { title: "SPI", subtitle: "Schedule Performance by Dept" },
    cei: { title: "CEI", subtitle: "Current Execution Index (CPI × SPI)" },
    eac: { title: "EAC", subtitle: "EAC vs BAC by Department" },
    bac: { title: "BAC", subtitle: "Budget at Completion" },
    vac: { title: "VAC", subtitle: "Variance at Completion" },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {activeMetric && metricModalConfigs[activeMetric] && metrics && (
        <MetricModal
          metric={activeMetric}
          title={metricModalConfigs[activeMetric].title}
          subtitle={metricModalConfigs[activeMetric].subtitle}
          onClose={() => setActiveMetric(null)}
          financials={modalFinancials}
          metrics={metrics}
          kpiAnalysis={latest?.output?.kpi_analysis || {}}
          reportId={latest?.id}
          workPackages={modalWorkPackages}
        />
      )}
      {/* Nav — 4 tabs matching Figma */}
      <ProjectNav projectId={projectId!} active="dashboard" />

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/select-project")}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                All Projects
              </button>
              <h1 className="text-2xl font-bold text-gray-900">DASHBOARD</h1>
              <span className="px-2 py-0.5 bg-green-50 text-green-600 text-xs font-semibold rounded border border-green-200">ACTIVE</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {latest?.quarter || "—"} · Last generated {latest ? new Date(latest.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"} · {name}
            </p>
          </div>
        </div>

        {/* Top 4 cards */}
        <div className="grid grid-cols-4 gap-4 mb-5">

          {/* Latest Report */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="w-8 h-8 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Latest Report</p>
            <p className="text-2xl font-bold text-gray-900">{latest?.quarter || "None"}</p>
            <p className="text-xs text-gray-500 mt-1">{latest?.version_label} · by {name}</p>
            {latest && (
              <div className="flex items-center gap-1.5 mt-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span className="text-xs text-blue-500 capitalize">
                  {latest.status} · {new Date(latest.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
            )}
            {latest && (
              <button onClick={() => navigate(`/reports/${latest.id}`)}
                className="mt-3 text-xs text-blue-600 hover:underline flex items-center gap-1">
                View Report →
              </button>
            )}
          </div>

          {/* Report History */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="w-8 h-8 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
              </svg>
            </div>
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Report History</p>
            <p className="text-2xl font-bold text-gray-900">{reports.length} Versions</p>
            <p className="text-xs text-gray-500 mt-1">
              Last: {latest ? new Date(latest.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
            </p>
            {reports.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span className="text-xs text-blue-500">Spanning {Math.min(reports.length, 4)} quarters</span>
              </div>
            )}
            <button onClick={() => navigate("/reports")}
              className="mt-3 text-xs text-blue-600 hover:underline flex items-center gap-1">
              View all versions →
            </button>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Quick Stats</p>
              {programStatus && (
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium border ${statusStyle}`}>
                  ● {programStatus}
                </span>
              )}
            </div>
            <div className="space-y-2.5 mb-3">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Worst Schedule Slip</span>
                <span className="text-xs font-semibold text-gray-900">
                  {maxSlipDays != null ? `+${maxSlipDays} days` : "—"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Slipped Work Packages</span>
                <span className="text-xs font-semibold text-gray-900">
                  {slippedCount > 0 ? `${slippedCount} WPs` : "—"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Earned to Date</span>
                <span className="text-xs font-semibold text-gray-900">
                  {earnedPct != null ? `${earnedPct}%` : "—"}
                </span>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-2">CPI Health Scale</p>
              {/* Bar spans 0.0 → 1.2. Thresholds: 0.90 = 75%, 0.95 = 79.2% */}
              <div className="relative h-3 rounded-full"
                style={{ background: "linear-gradient(to right, #ef4444 0%, #ef4444 75%, #f59e0b 75%, #f59e0b 79.2%, #22c55e 79.2%, #22c55e 100%)" }}>
                {cpiPct != null && (
                  <div
                    title={`CPI: ${cpi?.toFixed(3)}`}
                    className="group absolute w-2.5 h-2.5 bg-white border-2 border-gray-800 rounded-full shadow-md cursor-default"
                    style={{ left: `${cpiPct}%`, top: "50%", transform: "translate(-50%, -50%)" }}
                  >
                    {/* Tooltip */}
                    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 hidden group-hover:flex flex-col items-center pointer-events-none z-10">
                      <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap font-medium">
                        CPI {cpi?.toFixed(3)}
                      </div>
                      <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900" />
                    </div>
                  </div>
                )}
              </div>
              <div className="relative mt-1" style={{ height: "16px" }}>
                <span className="absolute text-xs text-gray-400" style={{ left: "0%" }}>0.0</span>
                <span className="absolute text-xs text-gray-400 -translate-x-1/2" style={{ left: "75%" }}>0.90</span>
                <span className="absolute text-xs text-gray-400 -translate-x-full" style={{ left: "100%" }}>1.2</span>
              </div>
            </div>
          </div>
          {/* Generate CTA */}
          <div className="bg-blue-600 rounded-xl p-4 flex flex-col justify-between relative overflow-hidden cursor-pointer"
            onClick={() => navigate("/upload/" + projectId)}>
            <div>
              <p className="text-xs text-blue-200 uppercase tracking-wide mb-1">New Report</p>
              {(() => {
                const now = new Date();
                const day = now.getDate();
                const monthName = now.toLocaleString("en-US", { month: "long" });
                const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                const daysUntilNext = Math.ceil((nextMonth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                const daysOverdue = day - 1;
                if (day >= 15) return (
                  <div>
                    <p className="text-xl font-bold text-white leading-tight">{daysUntilNext} days left</p>
                    <p className="text-xs text-blue-200 mt-1">To generate next month's report</p>
                  </div>
                );
                const hasCurrentReport = reports?.some((r: any) => {
                  const d = new Date(r.created_at || r.updated_at || "");
                  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                });
                if (hasCurrentReport) return (
                  <div>
                    <p className="text-xl font-bold text-white leading-tight">✓ {monthName} Report Generated</p>
                    <p className="text-xs text-blue-200 mt-1">All caught up for this month</p>
                  </div>
                );
                return (
                  <div>
                    <p className="text-xl font-bold text-white leading-tight">{daysOverdue} days overdue</p>
                    <p className="text-xs text-blue-200 mt-1">{monthName} report not yet generated</p>
                  </div>
                );
              })()}
            </div>
            <button onClick={e => { e.stopPropagation(); navigate("/upload/" + projectId); }}
              className="mt-3 border-2 border-dashed border-blue-400 border-opacity-60 rounded-lg py-2 text-sm font-medium text-white hover:bg-blue-500 flex items-center justify-center gap-2 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload Files
            </button>
          </div>
        </div>

        {/* Bottom — Program Summary + Trends */}
        <div className="grid grid-cols-2 gap-4 mb-5">

          {/* Program Summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-gray-800">Program Summary — {latest?.quarter || "—"}</p>
              {metrics && (() => {
                const label = !cpi ? "—" : cpi >= 0.95 ? "On Track" : cpi >= 0.9 ? "Warn" : "At Risk";
                const labelColor = !cpi ? "text-gray-400" : cpi >= 0.95 ? "text-green-600" : cpi >= 0.9 ? "text-amber-500" : "text-red-500";
                const barColor  = !cpi ? "bg-gray-300" : cpi >= 0.95 ? "bg-green-500" : cpi >= 0.9 ? "bg-amber-400" : "bg-red-500";
                return (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">% Complete (Earned to Date):</span>
                    <span className={`text-xs font-bold ${labelColor}`}>
                      {earnedPct != null ? `${earnedPct}%` : "—"}
                    </span>
                    <span className={`text-xs font-medium ${labelColor}`}>{label}</span>
                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden ml-1">
                      <div className={`h-full rounded-full ${barColor}`} style={{ width: `${earnedPct ?? 0}%` }} />
                    </div>
                  </div>
                );
              })()}
            </div>
            {metrics ? (
              <div className="grid grid-cols-3 gap-3">
                {[
                  { metric: "cpi", label: "CPI", value: cpi?.toFixed(3), sub: "Cost Performance", color: cpi ? cpiColor(cpi) : "text-gray-900", dot: cpi ? cpiDot(cpi) : undefined,
                    delta: (cpiDelta != null && Math.abs(cpiDelta) >= 0.0005) ? `${cpiDelta > 0 ? "+" : ""}${cpiDelta.toFixed(3)} ${cpiDelta > 0 ? "↑" : "↓"}` : undefined,
                    deltaColor: (cpiDelta != null && Math.abs(cpiDelta) >= 0.0005) ? (cpiDelta > 0 ? "text-green-500" : "text-red-400") : undefined },
                  { metric: "spi", label: "SPI", value: spi?.toFixed(3), sub: "Schedule Performance", color: spi ? cpiColor(spi) : "text-gray-900", dot: spi ? cpiDot(spi) : undefined },
                  { metric: "cei", label: "CEI", value: (cpi && spi) ? (cpi * spi).toFixed(3) : "—", sub: "Cost Efficiency Index", color: (cpi && spi) ? cpiColor(cpi * spi) : "text-gray-900", dot: (cpi && spi) ? cpiDot(cpi * spi) : undefined },
                  { metric: "eac", label: "EAC", value: eac ? `$${(eac/1000000).toFixed(1)}M` : "—", sub: "Estimate at Completion", color: "text-gray-900" },
                  { metric: "bac", label: "BAC", value: bac ? `$${(bac/1000000).toFixed(1)}M` : "—", sub: "Budget at Completion", color: "text-gray-900" },
                  { metric: "vac", label: "VAC", value: vac ? `${vac < 0 ? "-" : ""}$${(Math.abs(vac)/1000000).toFixed(1)}M` : "—", sub: "Variance at Completion", color: vac && vac < 0 ? "text-red-500" : "text-green-600", dot: vac && vac < 0 ? "text-red-500" : vac ? "text-green-600" : undefined,
                    delta: (vacDelta != null && Math.abs(vacDelta) >= 50000) ? `${vacDelta > 0 ? "+" : "-"}$${(Math.abs(vacDelta)/1000000).toFixed(1)}M ${vacDelta > 0 ? "↑" : "↓"}` : undefined,
                    deltaColor: (vacDelta != null && Math.abs(vacDelta) >= 50000) ? (vacDelta > 0 ? "text-green-500" : "text-red-400") : undefined },
                ].map((m, i) => (
                  <div key={i} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1">
                        <p className="text-xs text-gray-400 uppercase">{m.label}</p>
                        {m.dot && <span className={`text-xs ${m.dot}`}>●</span>}
                      </div>
                      {latest?.id && (
                        <button
                          onClick={() => setActiveMetric(m.metric)}
                          title={`${m.label} reasoning`}
                          className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors flex-shrink-0"
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21h6"/><path d="M12 3a6 6 0 0 1 6 6c0 2.22-1.2 4.16-3 5.2V17a1 1 0 0 1-1 1H10a1 1 0 0 1-1-1v-2.8C7.2 13.16 6 11.22 6 9a6 6 0 0 1 6-6z"/>
                          </svg>
                        </button>
                      )}
                    </div>
                    <p className={`text-xl font-bold ${m.color}`}>{m.value}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{m.sub}</p>
                    {m.delta && <p className={`text-xs mt-1 ${(m as any).deltaColor || "text-gray-400"}`}>{m.delta}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No report data yet. Generate a report to see metrics.</p>
            )}
          </div>

          {/* Trends */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-900 mb-4">Trends</p>
            {trendData.length > 0 ? (
              <>
                {/* EAC Trend — lower is better (overrun shrinking) */}
                <div className="mb-5">
                  {(() => {
                    const vals = trendData.map((d: any) => d.eac).filter((v: any) => v != null);
                    const dir = vals.length >= 2 ? vals[vals.length-1] - vals[vals.length-2] : null;
                    return (
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-xs text-gray-500">EAC Trend</p>
                        {dir != null && <span className={`text-xs ${dir < 0 ? "text-green-500" : dir > 0 ? "text-red-500" : "text-gray-400"}`}>{dir < 0 ? "↘ Reducing" : dir > 0 ? "↗ Growing" : "→ Stable"}</span>}
                      </div>
                    );
                  })()}
                  <div className="flex gap-3 text-xs text-gray-400 mb-1">
                    {trendData.map((d: any, i: number) => <span key={i}>{d.eac != null ? `$${d.eac.toFixed(1)}M` : "—"}</span>)}
                  </div>
                  <ResponsiveContainer width="100%" height={80}>
                    <AreaChart data={trendData} margin={{ bottom: 0 }}>
                      <defs>
                        <linearGradient id="eacGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="quarter" tick={{ fontSize: 9, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                      <YAxis domain={["auto","auto"]} hide />
                      <Area type="monotone" dataKey="eac" stroke="#f59e0b" strokeWidth={2} fill="url(#eacGrad)" dot={{ r: 3, fill: "#f59e0b" }} />
                      <Tooltip formatter={(v: any) => `$${v?.toFixed(1)}M`} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                {/* CEI Trend — higher is better (CPI × SPI composite) */}
                <div>
                  {(() => {
                    const vals = trendData.map((d: any) => d.cei).filter((v: any) => v != null);
                    const dir = vals.length >= 2 ? vals[vals.length-1] - vals[vals.length-2] : null;
                    return (
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-xs text-gray-500">CEI Trend</p>
                        {dir != null && <span className={`text-xs ${dir > 0 ? "text-green-500" : dir < 0 ? "text-red-500" : "text-gray-400"}`}>{dir > 0 ? "↗ Improving" : dir < 0 ? "↘ Declining" : "→ Stable"}</span>}
                      </div>
                    );
                  })()}
                  <div className="flex gap-3 text-xs text-gray-400 mb-1">
                    {trendData.map((d: any, i: number) => <span key={i}>{d.cei?.toFixed(2) || "—"}</span>)}
                  </div>
                  <ResponsiveContainer width="100%" height={80}>
                    <AreaChart data={trendData} margin={{ bottom: 0 }}>
                      <defs>
                        <linearGradient id="ceiGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="quarter" tick={{ fontSize: 9, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                      <YAxis domain={["auto","auto"]} hide />
                      <Area type="monotone" dataKey="cei" stroke="#3b82f6" strokeWidth={2} fill="url(#ceiGrad)" dot={{ r: 3, fill: "#3b82f6" }} />
                      <Tooltip formatter={(v: any) => v?.toFixed(3)} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400">Run multiple reports to see trends.</p>
            )}
          </div>
        </div>

        {/* AI Report Feedback — matches Figma exactly */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-start justify-between mb-1">
            <div>
              <p className="text-sm font-semibold text-gray-900">AI REPORT FEEDBACK</p>
              <p className="text-xs text-gray-400 mt-0.5">Your feedback trains the AI agent for the next report generation cycle</p>
            </div>
            <button className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              ● AI Agent Loop · {new Date().toLocaleString("en-US", { month: "long" }).toUpperCase().slice(0,3)}-{new Date().getFullYear()} Queue
            </button>
          </div>

          <div className="flex items-start justify-between mt-4">
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Flag areas for improvement</p>
              <div className="flex flex-wrap gap-2">
                {FEEDBACK_TAGS.map(tag => (
                  <button key={tag} onClick={() => toggleTag(tag)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors
                      ${feedbackTags.includes(tag)
                        ? "bg-blue-50 border-blue-400 text-blue-700"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            <div className="ml-8 text-right flex-shrink-0">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Report Quality Rating</p>
              <div className="flex items-center gap-1 justify-end">
                {[1,2,3,4,5].map(star => (
                  <button key={star} onClick={() => setFeedbackRating(star)}>
                    <svg className={`w-5 h-5 ${star <= feedbackRating ? "text-amber-400" : "text-gray-200"}`}
                      fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">{feedbackRating} of 5 — {feedbackRating >= 4 ? "Good, minor issues" : feedbackRating >= 3 ? "Acceptable" : "Needs improvement"}</p>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Detailed notes for AI agent</p>
            <textarea
              value={feedbackNote}
              onChange={e => setFeedbackNote(e.target.value)}
              placeholder="CPI variance looks inflated — verify against actuals from March. Milestone 4 was marked at risk incorrectly, it was completed on Feb 28."
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none placeholder-gray-300"
            />
          </div>

          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-gray-400">
              Feedback is securely logged and fed to the AI agent before {new Date().toLocaleString("en-US", { month: "long" })} begins
            </p>
            {feedbackSubmitted ? (
              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Feedback submitted
              </span>
            ) : (
              <button
                onClick={async () => {
                  if (getProjectRole(projectId) === "viewer" || !latest?.id) return;
                  try {
                    await api.post("/api/feedback", {
                      report_id:  latest.id,
                      project_id: projectId || null,
                      rating:     feedbackRating,
                      tags:       feedbackTags,
                      note:       feedbackNote || null,
                    });
                    setFeedbackSubmitted(true);
                  } catch {
                    setFeedbackSubmitted(true); // still mark done locally
                  }
                }}
                disabled={getProjectRole(projectId) === "viewer"}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                Submit To AI Agent
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
